import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosRadioGroup, type AerosRadioGroupProps } from "../AerosRadioGroup"

import type { Option } from "@/types/form-fields"

type FormBooleanRadioGroupVariant = "true-false" | "yes-no" | "active-inactive"

const BOOLEAN_RADIO_GROUP_LABELS_MAP = new Map([
  ["true-false", { affirmative: "True", negative: "False" }],
  ["yes-no", { affirmative: "Yes", negative: "No" }],
  ["active-inactive", { affirmative: "Active", negative: "Inactive" }],
])

interface FormBooleanRadioGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>
  extends Omit<AerosRadioGroupProps<string>, "name" | "defaultValue" | "onChange" | "options"> {
  control: Control<TFieldValues>
  name: TName
  label: string
  variant?: FormBooleanRadioGroupVariant
  disabled?: boolean
  onChange?: (value: boolean) => void
}

export const FormBooleanRadioGroup = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  variant = "true-false",
  disabled,
  helperText,
  onChange,
  ...props
}: FormBooleanRadioGroupProps<TFieldValues, TName>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: string) => {
    const boolValue = JSON.parse(value) as boolean
    field.onChange(boolValue)
    onChange?.(boolValue)
  }

  const labels = BOOLEAN_RADIO_GROUP_LABELS_MAP.get(variant)!
  const options: Option<string>[] = [
    { value: "true", label: labels.affirmative, disabled },
    { value: "false", label: labels.negative, disabled },
  ]

  return (
    <AerosRadioGroup<string>
      {...props}
      {...field}
      value={field.value === undefined ? "" : String(field.value)}
      row
      label={label}
      options={options}
      onChange={handleChange}
      error={!!error}
      helperText={error?.message ?? helperText}
    />
  )
}
